import { State } from '@sbercloud/uikit-product-calculator';
import { ChangeEvent, useRef } from 'react';
import { useForm } from 'react-hook-form';
import {
  MessageErrorOnFormSubmit,
  MessageFormSubmitted,
} from 'src/components/Form';
import { useAnalytics } from 'src/hooks/useAnalytics';
import { useOnClickOutside } from 'src/hooks/useOnClickOutside';
import { useSubmitForm } from 'src/hooks/useSubmitForm';
import { useTryFormErrorsCheck } from 'src/hooks/useTryFormErrorsCheck';
import { DataFromFormInputs } from 'src/types';
import { Button } from 'src/uikit/Button';
import { Checkbox } from 'src/uikit/Form/Checkbox';
import { Input } from 'src/uikit/Form/Input';
import { InputType, ValidationType } from 'src/uikit/Form/Input/constants';
import { TextArea } from 'src/uikit/Form/TextArea';
import { UserAgreementCheckboxes } from 'src/uikit/Form/UserAgreement';
import { CrossIcon } from 'src/uikit/icons/CrossIcon';
import { dataTestAttrProp } from 'src/utils/dataTestAttrProp';

import s from './Form.module.scss';

function beAdapter(state: State) {
  const stateForBe = state;
  return stateForBe;
}

function Form({ modalClose, state }: { modalClose: () => void; state: State }) {
  const {
    register,
    trigger,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    resetField,
    watch,
    setValue,
  } = useForm<DataFromFormInputs>({
    mode: 'onChange',
  });
  const { submitForm, formStatus, setFormStatus, formMessage, resultCode } =
    useSubmitForm();

  const {
    fillFormField,
    tryFormAnalytics,
    successfullSubmissionFormAnalytics,
  } = useAnalytics();
  useTryFormErrorsCheck(errors, isSubmitting, 'calculator');
  const ref = useRef<HTMLFormElement | null>(null);

  useOnClickOutside(ref, modalClose);

  // TODO можно задать тип в соответствии с полями, не так много данных в форме, чтобы уходить в абстрактный тип
  async function onSubmit(data: DataFromFormInputs) {
    const stateOfCalc = JSON.parse(JSON.stringify(state));
    const stateForBe = beAdapter(stateOfCalc);
    const other_consents = data.site_services_consent
      ? ['site_services_consent']
      : ['online_event_consent'];

    if (data.site_services_consent) {
      delete data.site_services_consent;
    } else {
      delete data.online_event_consent;
    }

    const dataToSend = {
      ...data,
      ...(data?.isNaturalPerson && typeof data.isNaturalPerson === 'string'
        ? { isNaturalPerson: JSON.parse(data.isNaturalPerson) }
        : {}),
      ...(data?.subscribeForMailing &&
      typeof data.subscribeForMailing === 'string'
        ? { subscribeForMailing: JSON.parse(data.subscribeForMailing) }
        : {}),
      other_consents,
      calcData: stateForBe,
      userAgreement: true,
    };

    await submitForm({
      backendPatchInput: '/forms/v1/consultation_calc',
      dataFromFormInputs: dataToSend,
      allFormSentFormType: 'calculator',
      onSuccess: () => {
        tryFormAnalytics({ formType: 'calculator', action: 'Отправлено' });
        successfullSubmissionFormAnalytics({
          zoneclick: 'B',
          formname: 'calculator',
        });

        setFormStatus('idle');
        reset();
      },

      onError: () => {
        tryFormAnalytics({
          formType: 'calculator',
          action: 'Ошибка',
          errorCode: resultCode,
        });
      },
    });
  }

  function textAreaValidate(e: ChangeEvent<HTMLTextAreaElement>) {
    if (e.target.value.length > 1000) {
      const substrValue = e.target.value.substring(0, 1000);
      setValue('comment', substrValue);
    }
  }

  const isCompanyAndPositionRequired = !watch('isNaturalPerson');

  return (
    <>
      {formStatus === 'success' ? (
        <MessageFormSubmitted
          title="Спасибо за заявку, мы скоро свяжемся с вами"
          buttonText={'Закрыть'}
          onClickOnReturnButton={modalClose}
        />
      ) : (
        <form
          {...dataTestAttrProp('form')}
          className={s.consultationForm}
          onSubmit={handleSubmit(onSubmit)}
          ref={ref}
        >
          <div className={s.header}>
            <h2 className={s.title}>Заявка на консультацию</h2>
            <div className={s.closeWrapper} onClick={modalClose}>
              <CrossIcon className={s.close} />
            </div>
          </div>
          <div className={s.consultationFormWrapper}>
            <div className={s.inputsWrapper}>
              <Input
                name="name"
                label={'Имя'}
                inputType={InputType.Text}
                validationType={ValidationType.Name}
                required
                errors={errors}
                register={register}
                resetField={resetField}
                className={s.input}
                onBlur={(event) => {
                  if (event.currentTarget.value) {
                    fillFormField({
                      formType: 'popup',
                      fieldName: 'name',
                      popupPlace: 'body',
                      buttonId: 'calculator-body',
                    });
                  }
                }}
              />
              <Input
                name="phone"
                inputType={InputType.Phone}
                label={'Телефон'}
                required
                errors={errors}
                register={register}
                resetField={resetField}
                className={s.input}
                onBlur={(event) => {
                  if (event.currentTarget.value) {
                    fillFormField({
                      formType: 'popup',
                      fieldName: 'tel',
                      popupPlace: 'body',
                      buttonId: 'calculator-body',
                    });
                  }
                }}
              />
            </div>
            <Input
              name="email"
              inputType={InputType.Text}
              validationType={ValidationType.Email}
              label={'Электронная почта'}
              errors={errors}
              register={register}
              resetField={resetField}
              required
              className={s.input}
              onBlur={(event) => {
                if (event.currentTarget.value) {
                  fillFormField({
                    formType: 'popup',
                    fieldName: 'email',
                    popupPlace: 'body',
                    buttonId: 'calculator-body',
                  });
                }
              }}
            />
            <Checkbox
              className={s.checkbox}
              errors={errors}
              required={false}
              register={register}
              name="isNaturalPerson"
              value="true"
              label={'Я физлицо'}
            />

            <div className={s.inputsWrapper}>
              <Input
                name="company"
                validationType={ValidationType.Company}
                inputType={InputType.Text}
                label={'Название компании'}
                required={isCompanyAndPositionRequired}
                errors={errors}
                register={register}
                resetField={resetField}
                className={s.input}
                onBlur={() => {}}
              />
              <Input
                name="position"
                validationType={ValidationType.Position}
                inputType={InputType.Text}
                label={'Должность'}
                errors={errors}
                register={register}
                resetField={resetField}
                required={isCompanyAndPositionRequired}
                className={s.input}
                onBlur={() => {}}
              />
            </div>
            <TextArea
              label={'Комментарий'}
              name="comment"
              required={false}
              errors={errors}
              register={register}
              resetField={resetField}
              className={s.textarea}
              style={{ maxHeight: '452px' }}
              onChange={textAreaValidate}
              maxLength={1000}
              onBlur={() => {}}
            />
            <UserAgreementCheckboxes
              errors={errors}
              register={register}
              agreementName={'site_services_consent'}
              sendFormFillAnalytics={() => {}}
            />
            <Button
              fullWidth
              isDisabled={formStatus === 'sending'}
              analyticsOptions={{
                action: 'click',
                clickZone: 'body',
                clickElement: 'calculator',
                clickContent: 'Отправить',
                uniqueId: 'calculator-form-submit-button',
                transitionType: 'inside-link',
              }}
              onClick={() => trigger()}
            >
              Отправить
            </Button>
            {formStatus === 'error' && (
              <MessageErrorOnFormSubmit text={formMessage} />
            )}
          </div>
        </form>
      )}
    </>
  );
}

export { Form };
